import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG = "/hero.jpeg";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const blockY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative overflow-hidden px-6 pt-32 pb-16 lg:px-12 lg:pt-40 lg:pb-24"
      data-testid="hero-section"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 top-24 select-none font-display text-[26rem] italic leading-none text-[#F3E6E1] hidden lg:block"
      >
        Her
      </span>
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-8 text-xs font-semibold uppercase tracking-[0.4em] text-[#6F665F]"
            data-testid="hero-eyebrow"
          >
            Luxury Press-On Nail Couture
          </motion.p>
          <h1
            className="font-display text-[15vw] leading-[0.95] tracking-tight sm:text-7xl lg:text-[7.5rem]"
            data-testid="hero-headline"
          >
            <RevealLine delay={0.4}>A Label</RevealLine>
            <RevealLine delay={0.55}>
              of <em className="italic text-[#D8AFA4]">Her</em>
            </RevealLine>
            <RevealLine delay={0.7}>Own.</RevealLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 max-w-md text-base leading-relaxed text-[#6F665F] md:text-lg"
            data-testid="hero-tagline"
          >
            Her Label is not about telling women what to wear. It is about
            helping every woman create a label of her own — beginning at her
            fingertips.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <a
              href="#waitlist"
              data-testid="hero-cta-waitlist"
              className="rounded-full bg-[#26221F] px-9 py-4 text-xs font-semibold uppercase tracking-[0.3em] text-[#FAF7F2] transition-colors duration-300 hover:bg-[#D8AFA4] hover:text-[#26221F]"
            >
              Join the Waitlist
            </a>
            <a
              href="#verticals"
              data-testid="hero-cta-explore"
              className="text-xs font-semibold uppercase tracking-[0.3em] text-[#26221F] underline decoration-[#D8AFA4] decoration-2 underline-offset-8 transition-colors duration-300 hover:text-[#D8AFA4]"
            >
              Explore the Verticals
            </a>
          </motion.div>
        </div>
        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: blockY }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="absolute -left-6 -top-6 h-full w-full bg-[#EAE5F0] border border-[#26221F]/15"
            aria-hidden="true"
          />
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="relative overflow-hidden border border-[#26221F]/20"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HERO_IMG}
              alt="Woman flaunting pastel press-on nails"
              className="aspect-[1268/1241] w-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.figcaption
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.7 }}
            className="mt-4 text-[10px] font-semibold uppercase tracking-[0.35em] text-[#6F665F]"
          >
            The debut collection — coming soon
          </motion.figcaption>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="mx-auto mt-20 flex max-w-7xl justify-center"
        aria-hidden="true"
      >
        <span className="h-16 w-px animate-scroll-hint bg-[#26221F]/40" />
      </motion.div>
    </section>
  );
}
